import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { InputParam } from "../../../types/grid.ts";
import type { ColumnDescription, InitCache } from "../../../types/session.ts";
import { getFilter } from "./registry.ts";
import styles from "./advancedFilterBar.module.scss";

interface AdvancedFilterBarProps {
  params: string[];
  columns: string[];
  colInfoMap: Record<string, ColumnDescription>;
  hiddenFiltersSet: Set<string>;
  hiddenColumnsSet?: Set<string>;
  migratedFilters?: Record<string, string | null>;
  cache: InitCache;
  onFiltersChange: (filters: InputParam[]) => void;
  onSearch: () => void;
  clearSignal?: number;
  loadSignal?: number;
  externalValues?: InputParam[];
}

interface ResolvedFilter {
  key: string;
  info: ColumnDescription;
  isParameter: boolean;
  registration: NonNullable<ReturnType<typeof getFilter>>;
}

/**
 * Returns the set of lowercase column names that will be rendered
 * in the advanced bar (so the inline FilterBar can skip them).
 */
export function getAdvancedColumnKeys(
  columns: string[],
  colInfoMap: Record<string, ColumnDescription>,
  hiddenFiltersSet: Set<string>,
): Set<string> {
  const keys = new Set<string>();
  for (const col of columns) {
    const lc = col.toLowerCase();
    if (hiddenFiltersSet.has(lc)) continue;
    const info = colInfoMap[lc];
    if (!info?.FilterType) continue;
    if (info.Header && hiddenFiltersSet.has(info.Header.toLowerCase())) continue;
    if (info.FilterText && hiddenFiltersSet.has(info.FilterText.toLowerCase())) continue;
    const reg = getFilter(info.FilterType);
    if (reg?.parameterOnly) keys.add(lc);
  }
  return keys;
}

export function AdvancedFilterBar({
  params,
  columns,
  colInfoMap,
  hiddenFiltersSet,
  hiddenColumnsSet,
  migratedFilters,
  cache,
  onFiltersChange,
  onSearch,
  clearSignal,
  loadSignal,
  externalValues,
}: AdvancedFilterBarProps) {
  const [filterState, setFilterState] = useState<Record<string, InputParam[]>>({});
  const filterStateRef = useRef(filterState);
  filterStateRef.current = filterState;
  const suppressNotify = useRef(false);
  const lastClearSignal = useRef(clearSignal);
  // Bumped on clear so child filters remount and lose their internal state.
  const [clearBump, setClearBump] = useState(0);
  // Tracks whether a clear happened after the most recent load — disables the
  // loadedState merge so children see the cleared empty state instead of the
  // stale loaded values.
  const loadSupersededByClear = useRef(false);

  // Clear all state when clearSignal changes
  if (clearSignal !== lastClearSignal.current) {
    lastClearSignal.current = clearSignal;
    setFilterState({});
    setClearBump(b => b + 1);
    loadSupersededByClear.current = true;
  }

  // secondary-param → primary filter key. Stops secondary params (e.g.
  // sGroupValue) from landing in their own orphan bucket that duplicates
  // the primary filter's emit and survives clears. Iterates both function
  // parameters AND grid columns because parameterOnly filters (like
  // FilterGroupBy on sGroupBy) reach the bar via the column path, not params.
  // Recomputes when any of those change so URL-restored filters get re-routed
  // once the registry/columns are known.
  const secondaryMap = useMemo(() => {
    const map: Record<string, string> = {};
    const visit = (rawName: string) => {
      const stripped = rawName.startsWith("@") ? rawName.substring(1) : rawName;
      const lc = stripped.toLowerCase();
      const info = colInfoMap[lc];
      if (!info?.FilterType) return;
      const reg = getFilter(info.FilterType);
      if (!reg?.secondaryParams) return;
      for (const sec of reg.secondaryParams) map[sec.toLowerCase()] = lc;
    };
    for (const p of params) visit(p);
    for (const c of columns) visit(c);
    return map;
  }, [params, columns, colInfoMap]);

  // Snapshot of externalValues taken at the load tick — frozen from later
  // renders so user interaction between loads doesn't leak in.
  const loadedRaw = useMemo<InputParam[] | null>(() => {
    if (!loadSignal || !externalValues) return null;
    return externalValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSignal]);

  // Group the loaded snapshot by primary filter key. Strips @-prefix AND
  // trailing 0/1 suffixes (date-range) and applies secondaryMap so e.g.
  // sGroupValue rejoins sGroupBy. Re-runs when secondaryMap finalizes.
  const loadedState = useMemo(() => {
    if (!loadedRaw) return null;
    const grouped: Record<string, InputParam[]> = {};
    for (const p of loadedRaw) {
      let key = (p.sColumn.startsWith("@") ? p.sColumn.substring(1) : p.sColumn).toLowerCase();
      if (/[01]$/.test(key)) {
        const base = key.slice(0, -1);
        if (colInfoMap[base]?.FilterType) key = base;
      }
      const primary = secondaryMap[key];
      if (primary) key = primary;
      (grouped[key] ??= []).push(p);
    }
    return grouped;
  }, [loadedRaw, secondaryMap, colInfoMap]);

  // When loadedState changes, push it into filterState and suppress the notify.
  // Initial value is -1 so the first render with any loadSignal >= 0 will trigger
  // a load if loadedState is present (catches the case where StrictMode remounts
  // after the parent has already bumped loadSignal).
  const lastLoadSignal = useRef<number | undefined>(-1);
  // pendingLoadOverride survives only across the single render where
  // setFilterState(loadedState) is scheduled but hasn't committed yet — its
  // job is to feed loadedState to remounted children (key includes loadSignal)
  // on that exact tick. After that render, the useEffect below nulls the ref
  // so subsequent renders use plain filterState. Without this clear, every
  // render after a load would re-apply loadedState OVER filterState, which
  // silently reverts every user click on a checkbox/toggle filter — see the
  // bug reported for "Include Test Kiosks" where check/uncheck updated the
  // URL but the control wouldn't visually flip.
  const pendingLoadOverride = useRef<Record<string, InputParam[]> | null>(null);
  if (loadSignal !== lastLoadSignal.current) {
    lastLoadSignal.current = loadSignal;
    loadSupersededByClear.current = false;
    if (loadSignal && loadedState) {
      suppressNotify.current = true;
      setFilterState(loadedState);
      pendingLoadOverride.current = loadedState;
      requestAnimationFrame(() => { suppressNotify.current = false; });
    }
  }

  // Effective state: spread loadedState over filterState ONLY on the consume
  // render (where setFilterState lags behind). After that, filterState wins.
  const effectiveState = (pendingLoadOverride.current && !loadSupersededByClear.current)
    ? { ...filterState, ...pendingLoadOverride.current }
    : filterState;

  useEffect(() => {
    pendingLoadOverride.current = null;
  });

  // Resolve which filters to show
  const resolved = useMemo(() => {
    const result: ResolvedFilter[] = [];
    const seen = new Set<string>();

    // Check if a filter should be hidden by matching ColumnName, Header, or FilterText
    const isHidden = (lc: string, info?: ColumnDescription | null) => {
      if (hiddenFiltersSet.has(lc)) return true;
      if (!info) return false;
      if (info.Header && hiddenFiltersSet.has(info.Header.toLowerCase())) return true;
      if (info.FilterText && hiddenFiltersSet.has(info.FilterText.toLowerCase())) return true;
      return false;
    };

    // 1. Function parameters (from Params + functionParameters)
    for (const param of params) {
      const stripped = param.startsWith("@") ? param.substring(1) : param;
      const lc = stripped.toLowerCase();
      const uc = stripped.toUpperCase();

      if (seen.has(lc)) continue;
      if (migratedFilters && typeof migratedFilters === "object" && uc in migratedFilters && migratedFilters[uc] === null) continue;

      const info = colInfoMap[lc];
      if (!info?.FilterType) continue;
      // NOTE: HiddenFilters is intentionally NOT applied to function parameters.
      // Legacy WebAdmin2 only hid *column* filters (it keyed the param path off
      // the filter-type name, which never matches a HiddenFilters entry), so a
      // report with both a column and a parameter named e.g. "KioskID" and
      // HiddenFilters=["KioskID"] hid the column but kept the parameter filter.
      // Params are resolved first, so the matching column is then deduped away
      // and the parameter filter remains — matching legacy.

      const registration = getFilter(info.FilterType);
      if (!registration) continue;

      seen.add(lc);
      result.push({ key: lc, info, isParameter: true, registration });
    }

    // 2. Regular columns with parameterOnly filter types (e.g., date columns)
    for (const col of columns) {
      const lc = col.toLowerCase();
      if (seen.has(lc)) continue;

      const info = colInfoMap[lc];
      if (!info?.FilterType) continue;
      if (isHidden(lc, info)) continue;

      const registration = getFilter(info.FilterType);
      if (!registration?.parameterOnly) continue;

      seen.add(lc);
      result.push({ key: lc, info, isParameter: false, registration });
    }

    // 3. Hidden columns whose filters are NOT hidden — promote inline filters
    //    (e.g. FilterTextBoxLike on a hidden column) to the advanced bar
    if (hiddenColumnsSet) {
      for (const col of columns) {
        const lc = col.toLowerCase();
        const uc = col.toUpperCase();
        if (seen.has(lc)) continue;
        if (!hiddenColumnsSet.has(lc)) continue;   // only hidden columns
        if (migratedFilters && typeof migratedFilters === "object" && uc in migratedFilters && migratedFilters[uc] === null) continue;

        const info = colInfoMap[lc];
        if (!info?.FilterType) continue;
        if (isHidden(lc, info)) continue;

        const registration = getFilter(info.FilterType);
        if (!registration) continue;

        seen.add(lc);
        result.push({ key: lc, info, isParameter: false, registration });
      }
    }

    return result;
  }, [params, columns, colInfoMap, hiddenFiltersSet, hiddenColumnsSet, migratedFilters]);

  // Notify parent whenever filterState changes
  useEffect(() => {
    if (suppressNotify.current) return;
    const all: InputParam[] = [];
    for (const params of Object.values(filterState)) {
      all.push(...params);
    }
    onFiltersChange(all);
  }, [filterState, onFiltersChange]);

  const handleFilterChange = useCallback(
    (key: string, value: InputParam[]) => {
      setFilterState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  if (resolved.length === 0) return null;

  return (
    <div className={styles.bar}>
      {resolved.map(({ key, info, isParameter, registration }) => {
        const Component = registration.component;
        const label = info.FilterText || info.Header || info.ColumnName;
        return (
          <fieldset key={`${key}-${loadSignal}-${clearBump}`} className={styles.filterFieldset}>
            <legend className={styles.filterLegend}>{label}</legend>
            <Component
              info={info}
              isParameter={isParameter}
              value={effectiveState[key] ?? []}
              onChange={(v: InputParam[]) => handleFilterChange(key, v)}
              onSearch={onSearch}
              cache={cache}
            />
          </fieldset>
        );
      })}
    </div>
  );
}
